import { FormField, UserPicker, isolate } from '@batiflow/ui';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Tag,
  TagGroup,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import { TRANSMITTAL_LINK_DAYS } from '@batiflow/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmailAddress, type EmailDraft, type PersonOption } from './model';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"group":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"addRow":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"fgs5rwf","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"addField":{"Bh6795r":"fqerorx","xawz":"f1i9w3rh","Bgto7am":"fuzgk9b"},"error":{"sj55zd":"f1hcrxcs","Be2twd7":"fy9rknc"}});

export interface RecipientFieldsProps {
  /** The people of the space the sender can pick (never including the sender). */
  options: readonly PersonOption[];
  optionsFailed: boolean;
  people: readonly string[];
  onPeopleChange: (ids: string[]) => void;
  emails: readonly EmailDraft[];
  onEmailsChange: (emails: EmailDraft[]) => void;
  /** Message about the recipients as a whole (none, too many). */
  error: string | null;
}

/** People of the space (a picker) and e-mail addresses with an optional name. */
export function RecipientFields({
  options,
  optionsFailed,
  people,
  onPeopleChange,
  emails,
  onEmailsChange,
  error,
}: RecipientFieldsProps) {
  const { t } = useTranslation('transmittals');
  const styles = useStyles();
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);

  const add = () => {
    const email = address.trim().toLowerCase();
    if (email === '') return;
    if (!isEmailAddress(email)) {
      setAddressError(t('recipients.email.invalid'));
      return;
    }
    if (emails.some((draft) => draft.email === email)) {
      setAddressError(t('recipients.email.duplicate'));
      return;
    }
    onEmailsChange([...emails, { email, name: name.trim() }]);
    setAddress('');
    setName('');
    setAddressError(null);
  };

  return (
    <div className={styles.root}>
      <h3 className={styles.heading}>{t('recipients.title')}</h3>
      <div className={styles.group}>
        {optionsFailed ? (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>{t('recipients.people.loadFailed')}</MessageBarBody>
          </MessageBar>
        ) : null}
        <FormField label={t('recipients.people.label')} hint={t('recipients.people.hint')}>
          <UserPicker
            label={t('recipients.people.label')}
            options={options}
            value={people}
            onChange={onPeopleChange}
          />
        </FormField>
      </div>

      <div className={styles.group}>
        <h4 className={styles.heading}>{t('recipients.email.title')}</h4>
        <p className={styles.hint}>{t('recipients.email.hint', { days: TRANSMITTAL_LINK_DAYS })}</p>
        <div className={styles.addRow}>
          <div className={styles.addField}>
            <FormField label={t('recipients.email.address')} error={addressError}>
              <Input
                type="email"
                autoComplete="off"
                maxLength={254}
                value={address}
                onChange={(_event, data) => {
                  setAddress(data.value);
                  setAddressError(null);
                }}
                // Enter adds the address; it must not send the whole form.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    add();
                  }
                }}
              />
            </FormField>
          </div>
          <div className={styles.addField}>
            <FormField label={t('recipients.email.name')}>
              <Input
                autoComplete="off"
                maxLength={200}
                value={name}
                onChange={(_event, data) => {
                  setName(data.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    add();
                  }
                }}
              />
            </FormField>
          </div>
          <Button icon={<Add20Regular />} onClick={add}>
            {t('recipients.email.add')}
          </Button>
        </div>
        {emails.length > 0 ? (
          <TagGroup
            aria-label={t('recipients.email.listLabel')}
            onDismiss={(_event, data) => {
              onEmailsChange(emails.filter((draft) => draft.email !== data.value));
            }}
          >
            {emails.map((draft) => (
              <Tag
                key={draft.email}
                value={draft.email}
                shape="rounded"
                dismissible
                dismissIcon={{
                  'aria-label': t('recipients.email.remove', { name: isolate(draft.email) }),
                }}
                {...(draft.name ? { secondaryText: draft.name } : {})}
              >
                <bdi dir="ltr">{draft.email}</bdi>
              </Tag>
            ))}
          </TagGroup>
        ) : null}
      </div>
      {error ? (
        <span role="alert" className={styles.error}>
          {error}
        </span>
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/transmittals/recipient-fields.griffel.css";